import { Button } from "@material-ui/core";
import { Typography } from "@welldigital/components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getSubscriptionDetails,
} from "pages/ClinicalReviewPage/ClinicalReviewApi";
import {
  Products as ProductType,
  SubscriptionDetailsType,
} from "../../Types";
import { formatDate } from "utils/time";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import "./subscription.scss";

interface PropsTypes {
  products: ProductType[];
  onSuccess: () => void;
}

type SubscriptionSummary = Pick<
  SubscriptionDetailsType,
  "status" | "cancelSubscriptionDate"
>;

const isCancelledSubscription = (status?: string) =>
  (status || "").trim().toLowerCase().startsWith("cancel");

const SubscriptionDetails = ({ products, onSuccess }: PropsTypes) => {
  const [selectedSubscription, setSelectedSubscription] = useState<ProductType | null>(
    null,
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [subscriptionDetailsById, setSubscriptionDetailsById] = useState<
    Record<string, SubscriptionSummary | undefined>
  >({});

  const subscriptionIds = useMemo(
    () =>
      Array.from(
        new Set(products.map((item) => item.subscriptionId).filter(Boolean)),
      ),
    [products],
  );

  useEffect(() => {
    let isMounted = true;

    if (subscriptionIds.length === 0) {
      setSubscriptionDetailsById({});
      return () => {
        isMounted = false;
      };
    }

    const loadSubscriptionDetails = async () => {
      const detailResponses = await Promise.allSettled(
        subscriptionIds.map((subscriptionId) =>
          getSubscriptionDetails(subscriptionId),
        ),
      );

      if (!isMounted) {
        return;
      }

      setSubscriptionDetailsById(
        subscriptionIds.reduce(
          (acc, subscriptionId, index) => {
            const response = detailResponses[index];
            acc[subscriptionId] =
              response.status === "fulfilled"
                ? {
                    status: response.value.status,
                    cancelSubscriptionDate: response.value.cancelSubscriptionDate,
                  }
                : {};
            return acc;
          },
          {} as Record<string, SubscriptionSummary | undefined>,
        ),
      );
    };

    loadSubscriptionDetails();

    return () => {
      isMounted = false;
    };
  }, [subscriptionIds]);

  const openCancelDialog = useCallback((product: ProductType) => {
    setSuccessMessage("");
    setSelectedSubscription(product);
  }, []);

  const closeCancelDialog = useCallback(() => {
    setSelectedSubscription(null);
  }, []);

  const handleSuccess = useCallback(
    (subscriptionId: string) => {
      setSelectedSubscription(null);
      setSubscriptionDetailsById((currentState) => ({
        ...currentState,
        [subscriptionId]: {
          ...currentState[subscriptionId],
          status: "cancel",
          cancelSubscriptionDate: new Date().toISOString(),
        },
      }));
      setSuccessMessage(`Subscription ${subscriptionId} cancelled successfully.`);
      onSuccess();
    },
    [onSuccess],
  );

  return (
    <>
      {!!successMessage && (
        <div className="w-subscription-details-feedback">
          <Typography className="w-subscription-details-feedbackText">
            {successMessage}
          </Typography>
        </div>
      )}
      {products
        ?.filter((item) => item.subscriptionId || item.subscriptionIssueNumber)
        .map((item) => {
          const hasIssueNumber =
            item.subscriptionIssueNumber !== undefined &&
            item.subscriptionIssueNumber !== null;
          const subscriptionDetails = item.subscriptionId
            ? subscriptionDetailsById[item.subscriptionId]
            : undefined;
          const isCancelled = isCancelledSubscription(subscriptionDetails?.status);
          const cancelledOnLabel = subscriptionDetails?.cancelSubscriptionDate
            ? ` on ${formatDate(subscriptionDetails.cancelSubscriptionDate, "dd/MM/yy")}`
            : "";

          return (
            <div
              key={`subs_details_${item.subscriptionId || item.orderItemId}`}
              className="w-subscription-details-container"
            >
              <div className="w-subscription-details-inline">
                {!!item.subscriptionId && (
                  <Typography className="w-subscription-details-label">
                    Subscription Id: {item.subscriptionId}
                  </Typography>
                )}
                {hasIssueNumber && (
                  <Typography className="w-subscription-details-label">
                    Issue {item.subscriptionIssueNumber} of {item.maxIssueLimit || 12}
                  </Typography>
                )}
              </div>
              <Typography className="w-subscription-details-productName">
                {item.productTitle}
              </Typography>
              {!!item.subscriptionId && isCancelled && (
                <div className="w-subscription-details-actions">
                  <Typography className="w-subscription-details-status w-subscription-details-statusCancelled">
                    {`Subscription cancelled${cancelledOnLabel}`}
                  </Typography>
                </div>
              )}
              {!!item.subscriptionId && !isCancelled && subscriptionDetails && (
                <div className="w-subscription-details-actions">
                  <Button
                    variant="outlined"
                    className="button-secondary w-subscription-details-cancelButton"
                    onClick={() => openCancelDialog(item)}
                  >
                    Cancel subscription
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      {!!selectedSubscription?.subscriptionId && (
        <CancelSubscriptionDialog
          productTitle={selectedSubscription.productTitle}
          subscriptionId={selectedSubscription.subscriptionId}
          onClose={closeCancelDialog}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default SubscriptionDetails;
